import type {ReactNode} from 'react';
import React from 'react';
import {Link} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {AfriexDispute} from '@/types/AfriexDispute';
import * as formatDate from '@/utils/date';

import type {DisputeTableProps} from './DisputeTable';

const getFieldsToDisplay = (
  data: AfriexDispute,
): Record<string, ReactNode>[] => {
  return [
    {'Dispute ID': data.id},
    {
      'User ID': (
        <React.Fragment>
          <span className="mr-3">{data.userId}</span>
          <Link
            to={`/users/${data.userId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="ghost" className="no-padding no-margin">
              View User Profile
            </Button>
          </Link>
        </React.Fragment>
      ),
    },
    {
      'Payment ID': (
        <React.Fragment>
          <span className="mr-3">{data.paymentId}</span>
          <Link
            to={`/transactions/${data.paymentId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="ghost" className="no-padding no-margin">
              View Transaction
            </Button>
          </Link>
        </React.Fragment>
      ),
    },
    {Reason: data.reason},
    {Comments: data.userNote},
  ];
};

const DisputeSummary = ({
  data,
  handleUpdateDisputeModal,
}: {
  data: AfriexDispute;
  handleUpdateDisputeModal: DisputeTableProps['handleUpdateDisputeModal'];
}) => {
  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-10 py-5 sm:py-10 flex flex-col gap-3">
      <div>
        {getFieldsToDisplay(data)?.map((field, idx) => {
          const key = Object.keys(field)[0];
          const value = Object.values(field)[0];

          if (!value || (typeof value === 'string' && value.length === 0)) {
            return null;
          }
          return (
            <div
              key={idx}
              className="mb-2 py-2 px-3 flex gap-3 bg-gray-100 justify-between dark:bg-gray-900"
            >
              <p className="text-slate-500 text-sm dark:text-slate-100">
                {key}
              </p>
              <p className="text-sm font-medium text-gray-700 w-2/3 overflow-hidden whitespace-normal dark:text-gray-100">
                {value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Notes */}
      {data.notes?.length ? (
        <div className="w-full">
          <h3 className="font-semibold mb-2">Notes:</h3>
          <div>
            {data.notes.map((n, idx) => {
              return (
                <div
                  key={idx}
                  className="mb-2 py-2 px-3 flex gap-3 bg-gray-100 justify-between dark:bg-gray-900"
                >
                  <p className="text-slate-500 text-sm dark:text-slate-100">
                    {formatDate.DDMonYYYY(n.createdAt, true)}
                  </p>
                  <p className="text-sm font-medium text-gray-700 w-2/3 overflow-hidden whitespace-normal dark:text-gray-100">
                    {n.note}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Button */}
      <PermissionsProvider permission={AfriexAdminPermission.DisputeUpdate}>
        <div className="ml-auto">
          <Button
            name="handleUpdateDisputeModal"
            onClick={() => handleUpdateDisputeModal(data.id)}
            variant="solid"
            colorScheme="cyan"
          >
            Update Dispute
          </Button>
        </div>
      </PermissionsProvider>
    </div>
  );
};

export default DisputeSummary;
